import {
    SimplePresentation,
    SimplePresentationRunnerController
} from "../../core/winpresentation/SimplePresentationRunner";
import {HTH} from "../HTH";
import {gsap} from "gsap";
import {HTHSymbol} from "../symbols/HTHSymbol";
import {HTHEnhancerSymbol} from "../symbols/HTHEnhancerSymbol";
import {HTHHelper} from "../utils/HTHHelper";
import {HTHSafeSymbol} from "../symbols/HTHSafeSymbol";
import {HTHGameConfig} from "../HTHGameConfig";
import {HTHSound} from "../sound/HTHSound";
import {HTHXWaysSymbol} from "../symbols/HTHXWaysSymbol";

export class HTHXWaysPresentation implements SimplePresentation {

    constructor() {
    }

    shouldRun(): boolean {
        return HTH.model.isXWaysPresent && !HTH.model.isGodModeMaxWin;
    }

    start(controller: SimplePresentationRunnerController): void {
        const totalXWTl: gsap.core.Timeline = gsap.timeline({
            onComplete: () => {
                HTH.screenInteractive.setInteractiveState(false);
                HTH.symbolOverlay.removeChildren();
                controller.setSkipCallback();
                controller.finish();
            }
        });

        if (HTH.model.gameData?.betWayWins.length === 0) {
            HTH.screenInteractive.setInteractiveState(true);
            if (HTHGameConfig.isSkipped[0] && !HTHGameConfig.isSkipped[1]) {
                // console.log("already at 1st skip xwayspresentation");
                totalXWTl.timeScale(2);
            } else if (HTHGameConfig.isSkipped[1]) {
                // console.log("already at 2nd skip xwayspresentation");
                totalXWTl.timeScale(4);
                HTH.screenInteractive.setInteractiveState(false);
            }
        }

        HTH.screenInteractive.clickArea.on('pointerdown', () => {
            if (!HTHGameConfig.isSkipped[0] && !HTHGameConfig.isSkipped[1]) {
                // console.log("1st skip xwayspresentation");
                HTHGameConfig.isSkipped[0] = true;
                totalXWTl.timeScale(2);
            } else if (HTHGameConfig.isSkipped[0] && !HTHGameConfig.isSkipped[1]) {
                // console.log("2nd skip xwayspresentation");
                HTHGameConfig.isSkipped[1] = true;
                totalXWTl.timeScale(4);
                HTH.screenInteractive.setInteractiveState(false);
            }
        });
        if (HTH.model.gameData?.betWayWins.length > 0) {
            controller.setSkipCallback(() => { //keeping in if condition because it was getting click before interactive screen
                totalXWTl.totalProgress(1);
            });
        }
        const linesNr: number = HTH.model.potentialWinPosition.length,
            stepTime: number = 0.2;
        let duration: number = linesNr * stepTime + 0.3,
            startTime: number = 0;
        HTH.model.potentialWinPosition.forEach((lines: number[][], i: number) => {
            lines.forEach((line: number[]) => {
                const sym: HTHSymbol = HTH.reelArea.getSymbol(line);
                if (!sym.participatedInPotentialWin) {
                    totalXWTl.add(sym.playPotentialWinHighlight(duration), startTime);
                }
            });
            duration -= stepTime;
            startTime += stepTime;
            totalXWTl.add(() => {
                HTHSound.playTrack(HTHSound.XWAYS_POTENTIAL_WINHIGHLIGHT);
            }, "<");
        });

        totalXWTl.to(this, {duration: 0.05});
        totalXWTl.addLabel("highlightComp");

        const xwaysPositionPerReel: number[][][] = [[], [], [], [], [], []];
        HTH.model.xWaysSymbolPositions.forEach((pos, i) => {
            xwaysPositionPerReel[pos[0]].push(pos);
        })
        let count: number = 0;
        xwaysPositionPerReel.forEach((xWaysPositions, i) => {
            if (xWaysPositions.length > 0) {
                count++;
                const perReelTl: gsap.core.Timeline = gsap.timeline();
                xWaysPositions.forEach((value: number[], j) => {
                    const oneXWTl: gsap.core.Timeline = gsap.timeline();
                    const sym: HTHSymbol = HTH.reelArea.getSymbol([value[0], value[1]]);

                    const xwayOverlayCont: PIXI.Container = new PIXI.Container();
                    const xwayBgSpine: PIXI.spine.Spine = new PIXI.spine.Spine(HTH.assetLoader.getSpineAsset("XW_bg"));
                    xwayBgSpine.state.setAnimation(0, "normal", false);
                    const xwayOverlaySpine: PIXI.spine.Spine = new PIXI.spine.Spine(HTH.assetLoader.getSpineAsset("XW"));

                    if (sym.symName === "SA") {
                        (<HTHSafeSymbol>sym).addBgForSpecialSymbol(xwayBgSpine, xwayOverlaySpine);
                        oneXWTl.add(() => {
                            HTH.symbolOverlay.addChild(sym);
                            sym.position.x = sym.reelId * HTHGameConfig.symbolWidth;
                        }, 0)
                    } else {
                        oneXWTl.add(() => {
                            xwayOverlayCont.addChild(xwayBgSpine);
                            xwayOverlayCont.addChild(xwayOverlaySpine);
                            HTH.symbolOverlay.addChild(xwayOverlayCont);
                        }, 0);
                    }

                    xwayOverlaySpine.state.setAnimation(0, "normal", false);
                    HTHHelper.copyLocalPosition(xwayOverlayCont, sym);

                    if (sym.isEnhancerSymbol) {
                        oneXWTl.add(() => {
                            (<HTHEnhancerSymbol | HTHSafeSymbol>sym).revealSymbol(HTH.model.gameData.xWaysSymbol, HTH.model.gameData.xWaysSizes[value[0]][value[1]], true);
                            if (sym.symName === "EB") {
                                HTH.reelArea.ebSpinArea.changeSymbol(sym.reelId, HTH.model.gameData.xWaysSymbol, HTH.model.gameData.xWaysSizes[value[0]][value[1]]);
                            }
                        });
                        if (sym.symName === "SA" && HTH.model.gameData.xWaysSizes[value[0]][value[1]] > 1) {
                            oneXWTl.add((<HTHSafeSymbol>sym).showWaysIncrease(HTH.model.gameData.xWaysSizes[value[0]][value[1]]));
                        }
                    } else {
                        sym.symName = HTH.model.gameData.xWaysSymbol;
                        sym.symSize = HTH.model.gameData.xWaysSizes[value[0]][value[1]];
                        oneXWTl.add(HTH.reelArea.allReels[sym.reelId].reelView.switchSymbol(sym, sym.symName));
                    }
                    oneXWTl.add(HTHHelper.playSpineWithTimeline(xwayOverlaySpine, 0, "win"));
                    oneXWTl.add(HTHHelper.playSpineWithTimeline(xwayBgSpine, 0, "win"), "<");

                    oneXWTl.add(() => {
                        HTHSound.playTrack(HTHSound.XWAYS_REVEAL);
                    }, "<");


                    if (sym.symName === "SA") {
                        oneXWTl.to(xwayOverlaySpine, {duration: 0.1, alpha: 0})
                        oneXWTl.to(xwayBgSpine, {duration: 0.1, alpha: 0}, "<")
                        oneXWTl.add((<HTHSafeSymbol>sym).removeBgForSpecialSymbol(xwayBgSpine, xwayOverlaySpine), ">");
                        oneXWTl.add(() => {
                            (<HTHSafeSymbol>sym).movetoParticularMaskedLayer(HTH.reelArea.allReels[sym.reelId].reelView.reelLayers.safeLayer);
                            sym.position.x = 0;
                        })

                    } else {
                        oneXWTl.to(xwayOverlayCont, {duration: 0.1, alpha: 0});
                    }
                   // console.log("durrrr", oneXWTl.totalDuration());

                    perReelTl.add(oneXWTl, j === 0 ? 0 : ">-0.7");
                });
                totalXWTl.add(perReelTl, count === 1 ? "highlightComp" : ">-0.7");

            }
        });
    }

}